
import { useState, useRef } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, LoadingState, ErrorState } from "@/components/features/states";
import { TrustBadge } from "@/components/features/badges";
import { toast } from "sonner";
import { ShieldCheck, Upload, Check, Clock, X, FileText, UserCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const ID_TYPES = [
  { value: "national_id", labelKey: "trust.nationalId" },
  { value: "passport", labelKey: "trust.passport" },
  { value: "driving_license", labelKey: "trust.drivingLicense" },
];

export function TrustView() {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const qc = useQueryClient();
  const { t } = useTranslation();
  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);
  const selfieRef = useRef<HTMLInputElement>(null);

  const [idType, setIdType] = useState("national_id");
  const [idNumber, setIdNumber] = useState("");
  const [frontUrl, setFrontUrl] = useState<string | null>(null);
  const [backUrl, setBackUrl] = useState<string | null>(null);
  const [selfieUrl, setSelfieUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["trust"],
    queryFn: () => api.trust.me(),
  });
  const trust = data?.data?.trust;

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("trust.loadFailed")} onRetry={refetch} />;

  const readFile = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = async (file: File | undefined, setter: (v: string) => void) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error(t("trust.imageTooLarge"));
    const dataUrl = await readFile(file);
    setter(dataUrl);
  };

  const submit = async () => {
    if (!idNumber.trim()) return toast.error(t("trust.enterIdNumber"));
    if (!frontUrl) return toast.error(t("trust.uploadIdFront"));
    setLoading(true);
    const res = await api.trust.submit({
      idType,
      idNumber: idNumber.trim(),
      idFrontUrl: frontUrl,
      idBackUrl: backUrl || undefined,
      selfieUrl: selfieUrl || undefined,
    });
    setLoading(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(t("trust.submitted"));
    qc.invalidateQueries({ queryKey: ["trust"] });
    qc.invalidateQueries({ queryKey: ["auth"] });
    useAuthStore.getState().load();
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader
        title={t("trust.title")}
        subtitle={t("trust.subtitleRequired")}
      />

      {/* Status card */}
      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          {trust?.status === "verified" ? (
            <Check className="h-10 w-10 text-emerald-600" />
          ) : trust?.status === "pending" ? (
            <Clock className="h-10 w-10 text-amber-600" />
          ) : trust?.status === "rejected" ? (
            <X className="h-10 w-10 text-red-600" />
          ) : (
            <ShieldCheck className="h-10 w-10 text-primary" />
          )}
          <div className="flex-1">
            <p className="font-semibold">{t("trust.verificationStatus")}</p>
            <div className="mt-1"><TrustBadge status={trust?.status || "none"} /></div>
          </div>
        </CardContent>
      </Card>

      {trust?.status === "verified" && (
        <Card className="border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/30">
          <CardContent className="p-4 text-sm text-emerald-800 dark:text-emerald-200">
            <ShieldCheck className="mb-1 h-5 w-5" />
            {t("trust.verifiedFullDesc")}
          </CardContent>
        </Card>
      )}

      {trust?.status === "rejected" && (
        <Card className="border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30">
          <CardContent className="p-4 text-sm text-red-800 dark:text-red-200">
            <X className="mb-1 h-5 w-5" />
            {t("trust.rejectedFullDesc", { reason: trust.rejectionReason || t("trust.defaultRejectionReason") })}
          </CardContent>
        </Card>
      )}

      {(trust?.status === "none" || trust?.status === "rejected") && (
        <Card>
          <CardContent className="space-y-4 p-4">
            <div className="space-y-1.5">
              <Label>{t("trust.idType")}</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={idType}
                onChange={(e) => setIdType(e.target.value)}
              >
                {ID_TYPES.map((t_item) => (
                  <option key={t_item.value} value={t_item.value}>{t(t_item.labelKey)}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="idnum">{t("trust.idNumber")}</Label>
              <Input id="idnum" placeholder={t("trust.idNumberPlaceholder")} value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <UploadTile label={t("trust.idFrontLabel")} icon={FileText} fileRef={frontRef} url={frontUrl} onFile={(f) => handleFile(f, setFrontUrl)} required />
              <UploadTile label={t("trust.idBackLabel")} icon={FileText} fileRef={backRef} url={backUrl} onFile={(f) => handleFile(f, setBackUrl)} />
            </div>
            <UploadTile label={t("trust.selfieLabel")} icon={UserCircle} fileRef={selfieRef} url={selfieUrl} onFile={(f) => handleFile(f, setSelfieUrl)} full />

            <Button className="w-full" size="lg" onClick={submit} disabled={loading}>
              {loading ? t("trust.submitting") : t("trust.submitForVerification")}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              {t("trust.reviewNote")}
            </p>
          </CardContent>
        </Card>
      )}

      {trust?.status === "pending" && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
          <CardContent className="p-4 text-sm text-amber-800 dark:text-amber-200">
            <Clock className="mb-1 h-5 w-5" />
            {t("trust.pendingFullDesc")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function UploadTile({
  label,
  icon: Icon,
  fileRef,
  url,
  onFile,
  required,
  full,
}: {
  label: string;
  icon: React.ElementType;
  // React 19's useRef<HTMLInputElement>(null) returns RefObject<HTMLInputElement | null>,
  // so the prop must accept the nullable variant to stay type-safe.
  fileRef: React.RefObject<HTMLInputElement | null>;
  url: string | null;
  onFile: (f: File | undefined) => void;
  required?: boolean;
  full?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="mb-1 block text-sm font-medium">{label}{required && <span className="text-destructive"> *</span>}</label>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-sm transition hover:border-primary/50 ${
          url ? "border-primary/40 bg-primary/5" : "border-border"
        }`}
      >
        {url ? (
          <>
            <img src={url} alt={label} className="max-h-16 rounded" />
            <Check className="h-4 w-4 text-emerald-600" />
          </>
        ) : (
          <>
            <Icon className="h-6 w-6 text-muted-foreground" />
            <span className="text-muted-foreground">{t("trust.tapToUpload")}</span>
          </>
        )}
      </button>
    </div>
  );
}
